import type { ReactNode } from 'react'

import { ArrowRight } from 'lucide-react'
import FadeIn from '@/components/generals/fade-in'
import LineReveal from '@/components/generals/line-reveal'
import TransitionLink from '@/components/generals/transition-link'
import { siteConfig } from '@/constants/site'

const Closing = (): ReactNode => {
    const { hours, contact } = siteConfig

    return (
        <section id='visit' aria-labelledby='visit-title' className='relative overflow-hidden py-28 md:py-44'>
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-0 bg-[radial-gradient(55%_50%_at_80%_85%,rgb(201_171_124/0.08),transparent_70%)]'
            />

            <div className='shell relative grid gap-20 lg:grid-cols-12 lg:items-end'>
                <div className='lg:col-span-5'>
                    <p className='caps flex items-center gap-4 text-brass'>
                        <span>Opening hours</span>
                    </p>

                    <h2 id='visit-title' className='sr-only'>Visit L’Élite</h2>

                    <FadeIn delay={0.1}>
                        <dl className='mt-10 border-t border-ivory/10'>
                            {hours.map(row => (
                                <div key={row.days} className='flex items-baseline justify-between gap-6 border-b border-ivory/10 py-5'>
                                    <dt className='font-heading text-[1.6rem] leading-tight md:text-[1.9rem]'>
                                        {row.daysLong}
                                    </dt>
                                    <dd className='shrink-0 tabular-nums text-ivory/80'>
                                        {row.open} – {row.close}
                                    </dd>
                                </div>
                            ))}
                        </dl>

                        <p className='mt-6 max-w-sm text-[0.95rem] leading-relaxed text-ivory/55'>
                            Last seating ninety minutes before we close. The counter is kept for walk-ins every evening.
                        </p>
                    </FadeIn>
                </div>

                <div className='lg:col-span-6 lg:col-start-7'>
                    <TransitionLink href='/reserve' className='group block' aria-label='Reserve a table'>
                        <LineReveal
                            as='span'
                            lines={["Reserve", <em key='table'>a table.</em>]}
                            className='block font-heading text-[clamp(4rem,11vw,10.5rem)] leading-[0.86] tracking-[-0.04em] transition-colors duration-700 group-hover:text-brass-bright'
                        />

                        <span className='mt-10 flex items-center gap-5'>
                            <span className='h-px flex-1 origin-left scale-x-50 bg-brass/60 transition-transform duration-1000 ease-film group-hover:scale-x-100' />
                            <span className='flex size-14 items-center justify-center rounded-full border border-brass/60 text-brass transition-colors duration-500 group-hover:bg-brass group-hover:text-charcoal'>
                                <ArrowRight aria-hidden='true' className='size-5' strokeWidth={1.25} />
                            </span>
                        </span>
                    </TransitionLink>

                    <p className='mt-8 text-[0.95rem] leading-relaxed text-ivory/55'>
                        {contact.street}, {contact.area}
                        <span aria-hidden='true' className='mx-3 text-ivory/25'>/</span>
                        <a href={contact.phoneHref} className='tabular-nums text-ivory/75 transition-colors duration-500 hover:text-brass-bright'>
                            {contact.phone}
                        </a>
                    </p>
                </div>
            </div>
        </section>
    )
}

export default Closing
